package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.image.ImageCleanup
import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.image.SupportedImageFormats
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.infrastructure.error.logInfrastructureFailure
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.coroutines.CancellationException
import kotlin.time.Duration.Companion.hours
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

private const val TAG = "ImageStorageRepository"
private val SIGNED_URL_EXPIRATION = 24.hours

/**
 * Supabase Storage implementation of ImageStorageRepository.
 * Each [ImageTarget] carries its own bucket + path scheme + cleanup strategy.
 */
class SupabaseImageStorageRepository(
    private val supabaseClient: SupabaseClient
) : ImageStorageRepository {

    override suspend fun upload(target: ImageTarget, image: ImageData): Result<String> {
        return try {
            val extension = SupportedImageFormats.extensionFor(image.mimeType)
            val path = target.buildPath(extension)
            val bucket = supabaseClient.storage[target.bucket]

            // Au mieux : un échec ne laisse qu'une ancienne variante orpheline, et l'upload
            // écrase de toute façon le fichier au même chemin
            try {
                performCleanup(target)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                logInfrastructureFailure(TAG, "Nettoyage des anciennes images impossible avant upload", e)
            }

            bucket.upload(path, image.bytes) {
                upsert = true
                contentType = ContentType.parse(image.mimeType)
            }

            Result.success(path)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "image"))
        }
    }

    override suspend fun delete(target: ImageTarget): Result<Unit> {
        return try {
            performCleanup(target)
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "image"))
        }
    }

    /** [version] ne sert qu'aux décorateurs à cache : le jeton est signé sur le seul chemin. */
    override suspend fun getSignedUrl(imagePath: String, version: String?): Result<String> {
        return try {
            val bucket = supabaseClient.storage[bucketFor(imagePath)]
            val signedUrl = bucket.createSignedUrl(imagePath, SIGNED_URL_EXPIRATION)
            Result.success(signedUrl)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "image"))
        }
    }

    override suspend fun downloadImage(imagePath: String): Result<ImageData> {
        return try {
            val bucket = supabaseClient.storage[bucketFor(imagePath)]
            val bytes = bucket.downloadAuthenticated(imagePath)
            val extension = imagePath.substringAfterLast('.', missingDelimiterValue = "jpg")
            val mimeType = SupportedImageFormats.mimeFor(extension)
            val fileName = imagePath.substringAfterLast('/')
            Result.success(ImageData(bytes = bytes, mimeType = mimeType, fileName = fileName))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "image"))
        }
    }

    @OptIn(ExperimentalUuidApi::class)
    override suspend fun uploadAnnouncementImage(tableId: String, image: ImageData): Result<String> {
        return try {
            val target = ImageTarget.Announcement(tableId, Uuid.random().toString())
            val extension = SupportedImageFormats.extensionFor(image.mimeType)
            val path = target.buildPath(extension)
            val bucket = supabaseClient.storage[target.bucket]

            bucket.upload(path, image.bytes) {
                upsert = true
                contentType = ContentType.parse(image.mimeType)
            }

            val signedUrl = bucket.createSignedUrl(path, SIGNED_URL_EXPIRATION)
            Result.success(signedUrl)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "image"))
        }
    }

    /**
     * Supprime les images existantes de [target]. Un fichier ou un dossier absent n'est pas une
     * erreur pour l'API Storage (réponse 200, liste vide) : une exception signale toujours un vrai
     * problème (réseau, jeton, erreur serveur). Un refus de policy, lui, passe inaperçu : les
     * objets invisibles pour l'utilisateur sont simplement ignorés.
     */
    private suspend fun performCleanup(target: ImageTarget) {
        val bucket = supabaseClient.storage[target.bucket]
        val toDelete = when (val cleanup = target.cleanup()) {
            is ImageCleanup.Paths -> cleanup.paths
            is ImageCleanup.Folder -> bucket.list("${cleanup.folder}/")
                .filter {
                    it.name.startsWith("${cleanup.namePrefix}_") ||
                        it.name.startsWith("${cleanup.namePrefix}.")
                }
                .map { "${cleanup.folder}/${it.name}" }
        }
        if (toDelete.isNotEmpty()) bucket.delete(toDelete)
    }

    private fun bucketFor(imagePath: String): String = when {
        imagePath.startsWith("items/") ||
            imagePath.startsWith("gm_inventory/") ||
            imagePath.startsWith("premade_items/") ->
            ImageTarget.INVENTORY_BUCKET

        else -> ImageTarget.AVATARS_BUCKET
    }
}
